import { parseSnapProgress, type SnapProgressMetadata } from "~/types/report-progress";

export const REPORT_PARTIAL_MESSAGE_TYPE = "report_partial" as const;

/**
 * Validated shape of a `report_partial` WebSocket frame (epic #21124).
 * `data` is the complete cumulative canonical report, structurally the same
 * as the final `/result` payload — callers replace, never merge it.
 */
export interface ReportPartialFrame {
  id: string;
  sequence: number;
  /** `null` when absent or malformed — never blocks the frame itself. */
  progress: SnapProgressMetadata | null;
  data: Record<string, unknown>;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

/**
 * Defensively parse a raw WebSocket payload into a typed {@link ReportPartialFrame}.
 *
 * Returns `null` for anything that isn't a well-formed `report_partial` frame:
 * wrong/missing `message_type`, missing/empty `id`, a non-integer `sequence`,
 * or a missing/non-object `result.data`. A malformed `progress` does NOT
 * invalidate the frame — `parseSnapProgress` already degrades that to `null`
 * (never replaces previously valid progress, never fails the snapshot).
 */
export function parseReportPartialFrame(payload: unknown): ReportPartialFrame | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;

  if (obj.message_type !== REPORT_PARTIAL_MESSAGE_TYPE) return null;

  const id = obj.id;
  if (typeof id !== "string" || id.length === 0) return null;

  const sequence = obj.sequence;
  if (!isNonNegativeInteger(sequence)) return null;

  const result = obj.result;
  if (!result || typeof result !== "object") return null;
  const data = (result as Record<string, unknown>).data;
  if (!data || typeof data !== "object") return null;

  return {
    id,
    sequence,
    progress: parseSnapProgress(obj.progress),
    data: data as Record<string, unknown>,
  };
}
